import React from 'react';
import clsx from 'clsx';

import { FlyoverCancel } from '../FlyoverCancel/FlyoverCancel';
import { FlyoverStart } from '../FlyoverStart/FlyoverStart';
import { HideWhenFlyoverActive } from '../HideWhenFlyoverActive/HideWhenFlyoverActive';
import { Info } from '../Info/Info';
import { MapScale } from '../MapScale/MapScale';
import { MapTypeControl } from '../MapTypeControl/MapTypeControl';
import { MapViewControls } from '../MapViewControls/MapViewControls';
import { ShowWhenFlyoverActive } from '../ShowWhenFlyoverActive/ShowWhenFlyoverActive';

import styles from './UIControls.module.scss';

/* Renders map UI controls in the 4 corners of the map.
 * If an override is provided for a corner, it will be rendered instead of the default controls.
 * If null is provided for a corner, no controls will be rendered in that corner.
 * If undefined is provided for a corner, the default controls will be rendered in that corner.
 */

interface UIControlsProps {
  topLeftOverride?: React.ReactNode | null;
  topRightOverride?: React.ReactNode | null;
  bottomLeftOverride?: React.ReactNode | null;
  bottomRightOverride?: React.ReactNode | null;
  flyover?: boolean; // If true, and if not overridden, the flyover controls will be shown
}

export const UIControls = ({
  topLeftOverride,
  topRightOverride,
  bottomLeftOverride,
  bottomRightOverride,
  flyover = false
}: UIControlsProps) => {
  return (
    <>
      {topLeftOverride !== null && (
        <div className={clsx(styles.uiControls, styles.topLeft)}>
          {topLeftOverride || (
            <>
              <HideWhenFlyoverActive>
                <MapViewControls />
              </HideWhenFlyoverActive>
              <ShowWhenFlyoverActive>
                <FlyoverCancel />
              </ShowWhenFlyoverActive>
            </>
          )}
        </div>
      )}

      {topRightOverride !== null && (
        <div className={clsx(styles.uiControls, styles.topRight)}>
          {topRightOverride || <MapTypeControl />}
        </div>
      )}

      {bottomLeftOverride !== null && (
        <div
          className={clsx(styles.uiControls, styles.bottomLeft, {
            [styles.bottomLeftDefault as string]:
              bottomLeftOverride === undefined
          })} // apply extra styling only for default controls, required to position Info and MapScale with `flex-direction: column`
        >
          {bottomLeftOverride || (
            <HideWhenFlyoverActive>
              <Info />
              <MapScale />
            </HideWhenFlyoverActive>
          )}
        </div>
      )}

      {bottomRightOverride !== null && (
        <div className={clsx(styles.uiControls, styles.bottomRight)}>
          {bottomRightOverride ||
            (flyover ? (
              <HideWhenFlyoverActive>
                <FlyoverStart />
              </HideWhenFlyoverActive>
            ) : null)}
        </div>
      )}
    </>
  );
};
